// app/components/Plugins/Automations/AutomationsModal.tsx
'use client'

import React, { useEffect, useRef, useState } from 'react'
import Automations from './Automations'
import styles from './AutomationsModal.module.css'

interface AutomationsModalProps {
  isOpen: boolean
  onClose: () => void
}

const AutomationsModal: React.FC<AutomationsModalProps> = ({ isOpen, onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const [theme, setTheme] = useState<string>('dark')
  
  // Load theme from localStorage on component mount and listen for changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Initial theme load
      const savedTheme = localStorage.getItem('theme') || 'dark'
      setTheme(savedTheme)
      
      // Listen for theme changes in localStorage
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === 'theme') {
          setTheme(e.newValue || 'dark')
        }
      }
      
      // Listen for custom theme change events
      const handleThemeChange = (e: CustomEvent<{ isDark: boolean }>) => {
        setTheme(e.detail.isDark ? 'dark' : 'light')
      }
      
      window.addEventListener('storage', handleStorageChange)
      window.addEventListener('themeChange', handleThemeChange as EventListener)
      
      return () => {
        window.removeEventListener('storage', handleStorageChange)
        window.removeEventListener('themeChange', handleThemeChange as EventListener)
      }
    }
  }, [])

  // Handle click on overlay to close the modal
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Only close if the click is directly on the overlay (not its children)
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  // Handle escape key to close the modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey)
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  // Determine background color based on theme
  const bgColor = theme === 'light' ? '#f5f5f5' : '#1a1a1a'
  const headerBgColor = theme === 'light' ? '#e0e0e0' : '#252525'
  const textColor = theme === 'light' ? '#333' : '#fff'
  const borderColor = theme === 'light' ? '#ccc' : '#333'

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div 
        className={styles.modalContainer} 
        ref={modalRef}
        style={{ backgroundColor: bgColor, color: textColor }}
      >
        <div className={styles.modalHeader} style={{ backgroundColor: headerBgColor, borderColor: borderColor }}>
          <h2 style={{ color: textColor }}>Automations</h2>
          <div className={styles.headerButtons}>
            <button 
              className={styles.expandButton} 
              onClick={() => window.open(`${process.env.NEXT_PUBLIC_BASE_URL || ''}/automations`, '_blank')}
              title="Open in new tab"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h6v6"/>
                <path d="M10 14L21 3"/>
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
              </svg>
            </button>
            <button className={styles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>
        </div>
        <div className={styles.modalContent}>
          <Automations />
        </div>
      </div>
    </div>
  )
}

export default AutomationsModal